import { useState, useCallback } from 'react';
import { useSupabaseAdmin } from '../../../hooks/useSupabaseAdmin';
import { useAdminClientesAsesoria } from '../../../hooks/useAdminClientesAsesoria';
import type { AsesoriaPlan } from '../../../hooks/useSupabaseAdmin';
import AdminAsesoriaRecomendaciones from './AdminAsesoriaRecomendaciones';
import AdminAsesoriaCalendario from './AdminAsesoriaCalendario';
import AdminClientesAsesoria from './AdminClientesAsesoria';
import AdminArmario from './AdminArmario';
import AdminPerfilCliente from './AdminPerfilCliente';
import PlanHeader from './plans/PlanHeader';
import PlanForm from './plans/PlanForm';
import PlanTable from './plans/PlanTable';

import { useAdminAsesoriaContext } from '../../../context/AdminAsesoriaContext';

type SubTab = 'planes' | 'clientes' | 'perfil' | 'armario' | 'recomendaciones' | 'calendario';

const mockPlans: AsesoriaPlan[] = [
  {
    id: 1,
    name: 'Esencial',
    price: '1.200€',
    period: 'por temporada',
    description: 'Revisión completa de armario y definición de estilo base para quien quiere empezar con firmeza.',
    features: [
      'Análisis de perfil psicológico y comunicación personal',
      'Revisión del armario actual (hasta 60 prendas)',
      'Definición de paleta de color personal',
      'Creación de 12 looks estratégicos',
      'Lista de compras prioritaria',
      '1 sesión de seguimiento (2h)',
    ],
    cta: 'Reservar consulta',
    highlighted: false,
    sort_order: 0,
  },
  {
    id: 2,
    name: 'Premium',
    price: '3.800€',
    period: 'por temporada',
    description: 'Transformación integral con sistema digital y asesoría continua. El equilibrio perfecto.',
    features: [
      'Todo lo incluido en Esencial',
      'Acceso al sistema digital de armario inteligente',
      'Registro digital de todas las prendas',
      'Combinaciones inteligentes automatizadas',
      'Calendario de looks planificado',
      'Asesoría en corte de cabello y barba',
      'Protocolo de cuidado de piel personalizado',
      '4 sesiones de seguimiento (8h total)',
      'Personal shopper acompañado (4h)',
    ],
    cta: 'Solicitar evaluación',
    highlighted: true,
    sort_order: 1,
  },
  {
    id: 3,
    name: 'Integral',
    price: '7.500€',
    period: 'anual',
    description: 'Acompañamiento completo durante un año. Tu imagen, evolucionada bajo supervisión directa.',
    features: [
      'Todo lo incluido en Premium',
      'Diseño y confección de 3 prendas a medida',
      'Organización mensual del armario',
      'Actualización continua del sistema digital',
      'Asesoría en maquillaje y definición estética',
      'Planificación de looks para eventos especiales',
      'Sesiones trimestrales de revisión (12h total)',
      'Personal shopper ilimitado dentro del plan',
      'Línea directa con Anthony Sánchez',
    ],
    cta: 'Entrevista exclusiva',
    highlighted: false,
    sort_order: 2,
  },
];

const subTabs: { key: SubTab; label: string; icon: string }[] = [
  { key: 'planes', label: 'Planes', icon: 'ri-price-tag-3-line' },
  { key: 'clientes', label: 'Clientes', icon: 'ri-user-line' },
  { key: 'perfil', label: 'Perfil', icon: 'ri-id-card-line' },
  { key: 'armario', label: 'Armario', icon: 'ri-t-shirt-line' },
  { key: 'recomendaciones', label: 'Recomendaciones', icon: 'ri-lightbulb-line' },
  { key: 'calendario', label: 'Calendario', icon: 'ri-calendar-event-line' },
];

export default function AdminAsesoriaPlans() {
  const {
    selectedClienteId,
    setSelectedClienteId,
    activeSubTab,
    setActiveSubTab,
  } = useAdminAsesoriaContext();

  const { data: clientes } = useAdminClientesAsesoria();
  const currentCliente = clientes.find((c) => c.id === selectedClienteId);

  const {
    data,
    setData,
    add,
    update,
    remove,
    refresh,
    loading,
  } = useSupabaseAdmin<AsesoriaPlan>({
    table: 'asesoria_plans',
    initialData: mockPlans,
  });

  const [editing, setEditing] = useState<AsesoriaPlan | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const filtered = data.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.price.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (formData: FormData) => {
    setSaving(true);
    setFormError(null);
    const name = (formData.get('name') as string)?.trim();
    const price = (formData.get('price') as string)?.trim();
    const period = (formData.get('period') as string)?.trim();
    const description = (formData.get('description') as string)?.trim();
    const featuresRaw = (formData.get('features') as string)?.trim();
    const cta = (formData.get('cta') as string)?.trim();
    const highlighted = formData.get('highlighted') === 'on';

    if (!name || !price || !period) {
      setFormError('Nombre, precio y período son obligatorios.');
      setSaving(false);
      return;
    }

    const features = featuresRaw
      ? featuresRaw
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    const payload: Omit<AsesoriaPlan, 'id'> = {
      name,
      price,
      period,
      description,
      features,
      cta: cta || 'Solicitar',
      highlighted,
      sort_order: editing?.sort_order ?? data.length,
    };

    if (editing) {
      await update(editing.id, payload);
      setEditing(null);
    } else {
      await add(payload);
      setIsAdding(false);
    }
    setSaving(false);
  };

  const moveItem = useCallback(
    async (fromId: number, toId: number) => {
      if (fromId === toId) return;

      const sorted = [...data].sort((a, b) => a.sort_order - b.sort_order);
      const fromIndex = sorted.findIndex((d) => d.id === fromId);
      const toIndex = sorted.findIndex((d) => d.id === toId);
      if (fromIndex === -1 || toIndex === -1) return;

      sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, data.find((d) => d.id === fromId)!);

      const reordered = sorted.map((item, idx) => ({ ...item, sort_order: idx }));
      setData(reordered);

      const { supabase } = await import('../../../lib/supabase');
      for (const item of reordered) {
        await supabase
          .from('asesoria_plans')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id);
      }
    },
    [data, setData]
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLTableRowElement>,
    toId: number
  ) => {
    e.preventDefault();
    const fromId = Number(e.dataTransfer.getData('text/plain'));
    moveItem(fromId, toId);
    setDragOverId(null);
    setDraggingId(null);
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    setDraggingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Shared Client Selector Header */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
            <i className="ri-user-star-line text-lg" />
          </div>
          <div>
            <p className="font-sans text-brand-black text-sm font-medium">
              Cliente activo en Asesoría
            </p>
            <div className="flex items-center gap-3 mt-1">
              <div className="relative">
                <select
                  value={selectedClienteId || ''}
                  onChange={(e) => setSelectedClienteId(Number(e.target.value))}
                  className="appearance-none bg-white border border-brand-gray-200 rounded-lg pl-4 pr-10 py-2 font-sans text-sm focus:outline-none focus:border-brand-black cursor-pointer"
                >
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-gray-400">
                  <i className="ri-arrow-down-s-line" />
                </div>
              </div>
              {currentCliente && (
                <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage/20 text-brand-black">
                  {currentCliente.plan_contratado || 'Sin plan'}
                </span>
              )}
            </div>
          </div>
        </div>
        {currentCliente && (
          <div className="flex items-center gap-4 text-right lg:text-left">
            <div>
              <p className="font-sans text-brand-gray-400 text-xs">Email</p>
              <p className="font-sans text-brand-black text-sm">{currentCliente.email || '—'}</p>
            </div>
            <div>
              <p className="font-sans text-brand-gray-400 text-xs">Teléfono</p>
              <p className="font-sans text-brand-black text-sm">{currentCliente.telefono || '—'}</p>
            </div>
          </div>
        )}
      </div>

      {loading && activeSubTab === 'planes' && (
        <div className="flex items-center gap-2 text-brand-gray-500 text-sm bg-brand-gray-50 rounded-lg px-4 py-3">
          <i className="ri-loader-4-line animate-spin" />
          <span>Sincronizando con la nube…</span>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {subTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveSubTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-sans text-sm whitespace-nowrap transition-colors ${
              activeSubTab === t.key
                ? 'bg-brand-black text-white'
                : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black hover:border-brand-gray-400'
            }`}
          >
            <i className={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Pass selectedClienteId to sub-tabs that need it */}
      {activeSubTab === 'clientes' && <AdminClientesAsesoria />}
      {activeSubTab === 'perfil' && (
        <AdminPerfilCliente selectedClienteId={selectedClienteId} />
      )}
      {activeSubTab === 'armario' && (
        <AdminArmario selectedClienteId={selectedClienteId} />
      )}
      {activeSubTab === 'recomendaciones' && (
        <AdminAsesoriaRecomendaciones selectedClienteId={selectedClienteId} />
      )}
      {activeSubTab === 'calendario' && (
        <AdminAsesoriaCalendario selectedClienteId={selectedClienteId} />
      )}

      {activeSubTab === 'planes' && (
        <>
          <PlanHeader
            totalCount={data.length}
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1); }}
            onAdd={() => {
              setIsAdding(true);
              setEditing(null);
              setFormError(null);
            }}
            onRefresh={refresh}
          />

          {(isAdding || editing) && (
            <PlanForm
              editing={editing}
              onCancel={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
              }}
              onSave={handleSave}
              formError={formError}
              saving={saving}
            />
          )}

          <PlanTable
            plans={filtered}
            draggingId={draggingId}
            dragOverId={dragOverId}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onEdit={(plan) => {
              setEditing(plan);
              setIsAdding(false);
              setFormError(null);
            }}
            onDelete={(plan) => {
              if (confirm(`¿Eliminar el plan "${plan.name}"?`)) {
                remove(plan.id);
              }
            }}
            page={page}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
